import { inject, Injectable, signal } from '@angular/core';
import { BaseResponse } from '@ibc-shared/model/base-response.model';
import { catchError, Observable, of, tap } from 'rxjs';
import { CheckAuth, ConfirmResetPasswordModel, CurrentUser, LoginResponse } from '../models/auth.model';
import { AuthRepository } from '../repositories/auth.repository';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public readonly repo = inject(AuthRepository);

  public isLoggedIn = signal<boolean>(false);
  public currentUser = signal<CurrentUser | null>(null);

  public login(email: string, password: string): Observable<BaseResponse<LoginResponse>> {
    return this.repo.login(email, password).pipe(
      tap(res => {
        if (res.data?.auth) {
          this.isLoggedIn.set(true);
        }
      })
    );
  }

  public checkAuth(): Observable<BaseResponse<CheckAuth>> {
    return this.repo.check().pipe(
      tap(res => this.isLoggedIn.set(!!res.data?.auth)),
      catchError(() => {
        this.isLoggedIn.set(false);
        return of({
          data: {
            auth: false,
            message: 'Invalid Session',
          } as CheckAuth,
        });
      })
    );
  }

  public fetchCurrentUser() {
    return this.repo.getCurrentUser().pipe(
      tap((res: BaseResponse<CurrentUser>) => {
        const user: CurrentUser = res.data!;
        this.currentUser.set(user);
        this.isLoggedIn.set(true);
      }),
      catchError(() => {
        this.currentUser.set(null);
        this.isLoggedIn.set(false);
        return of(null);
      })
    );
  }

  public logout() {
    return this.repo.logout().pipe(
      tap(() => {
        this.isLoggedIn.set(false);
        this.currentUser.set(null);
      }),
      catchError(() => {
        this.isLoggedIn.set(false);
        this.currentUser.set(null);
        return of(null);
      })
    );
  }

  /* ---- RESET PASSWORD SECTION ---- */

  public resetPassword(email: string): Observable<BaseResponse<string>> {
    return this.repo.sendResetPassword(email);
  }

  public resetPasswordConfirmation(data: ConfirmResetPasswordModel) {
    return this.repo.confirmResetPassword(data);
  }
}
